/**
 * ObjectHeader — a reusable header component for any object kind (entity, domain, etc.)
 * rendering icon, badges, tags/chips, title, docref, and description.
 * Extracts the common pattern from EntityDetailView and DomainView.
 */
import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import { Chip } from './Chip.js';
import { DomainBadge } from '../views/DomainBadge.js';
import { LayerBadge } from '../views/LayerBadge.js';
import type { DomainLayer } from '@atlas-manifest/types';

export interface Badge {
  label: string;
  type: 'domain' | 'layer';
  /** Per-badge size override. Defaults to 'sm' (EntityDetailView's existing convention). */
  size?: 'sm' | 'md';
  /** Layer badges only: show the full layer name next to the "L1"-style label. Defaults to false. */
  showLabel?: boolean;
}

export interface ChipItem {
  label: string;
  tone?: 'good' | 'caution' | 'danger' | 'info';
  /** Tooltip text, passed through to Chip's title prop. Defaults to the label. */
  title?: string;
}

export interface ObjectHeaderProps {
  /** The object being rendered (Entity, Domain, Event, etc.). Used for type narrowing by caller. */
  object: unknown;
  /** Icon name (from Icon component's name prop). */
  icon: string;
  /** Aria label for the icon. */
  iconLabel: string;
  /** Override icon colour (e.g. a domain's colour token) — passed through to Icon's color prop. */
  iconColor?: string;
  /** Badges to render: domain badge, layer badge, etc. */
  badges: Badge[];
  /** Title text. */
  title: string;
  /** Firestore path or documentation reference. Accepts ReactNode (not just string) so a caller composing multiple pieces — e.g. a firestorePath prefix plus the docRef itself — can wrap the docRef portion in its own element, keeping it independently text-matchable in tests rather than fused into one opaque string. */
  docRef?: ReactNode;
  /** Optional description text below the title. */
  description?: string;
  /** Tags to render as chips (e.g., entity tags). */
  tags?: string[];
  /** Chips with optional tone (e.g., packGate, entity kind/lifecycle). */
  chips?: ChipItem[];
  /** Optional slot for action buttons (Edit, Simulate, etc.). */
  actions?: ReactNode;
}

export function ObjectHeader({
  object: _object,
  icon,
  iconLabel,
  iconColor,
  badges,
  title,
  docRef,
  description,
  tags = [],
  chips = [],
  actions,
}: ObjectHeaderProps): React.JSX.Element {
  return (
    <>
      <div className="entity-head">
        <Icon name={icon} size="lg" label={iconLabel} color={iconColor} />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: badges.length > 0 ? '8px' : '0' }}>
          {badges.map((badge, i) =>
            badge.type === 'domain' ? (
              <DomainBadge key={`badge-${i}`} domainId={badge.label} size={badge.size ?? 'sm'} />
            ) : badge.type === 'layer' ? (
              <LayerBadge key={`badge-${i}`} layer={badge.label as DomainLayer} size={badge.size ?? 'sm'} showLabel={badge.showLabel} />
            ) : null
          )}
        </div>
        <h1 className="entity-title mono">{title}</h1>
        {tags.map((tag) => (
          <Chip key={tag} label={tag} />
        ))}
        {chips.map((chip, i) => (
          <Chip key={`chip-${i}`} label={chip.label} tone={chip.tone} title={chip.title} />
        ))}
        {actions && <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>{actions}</div>}
      </div>
      {docRef && <div className="entity-path">{docRef}</div>}
      {description && <p className="entity-desc">{description}</p>}
    </>
  );
}
